import { Controller } from "@hotwired/stimulus"

function base64urlToBuffer(base64url) {
  // Handle non-string inputs
  if (typeof base64url !== 'string') {
    // If it's already a buffer/array, return it as is
    if (base64url instanceof Uint8Array || base64url instanceof ArrayBuffer) {
      return base64url
    }
    console.error('[passkeys] Invalid input to base64urlToBuffer:', base64url)
    throw new Error('Invalid base64url input')
  }
  
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '==='.slice((base64.length + 3) % 4)
  return Uint8Array.from(window.atob(padded), c => c.charCodeAt(0))
}

function bufferToBase64url(buffer) {
  const base64 = window.btoa(String.fromCharCode(...new Uint8Array(buffer)))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/,'')
}

export default class extends Controller {
  static targets = ["email"]
  static values = { registerUrl: String, registerVerifyUrl: String, loginOptionsUrl: String, loginVerifyUrl: String }

  async add() {
    try {
      if (!window.PublicKeyCredential || !navigator.credentials) {
        this.alert("Passkeys are not supported in this browser.")
        return
      }

      const res = await fetch(this.registerUrlValue, { credentials: "same-origin" })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message || "Failed to get registration options from server")
      }
      const options = await res.json()

      if (!options.challenge || typeof options.challenge !== 'string') {
        throw new Error("Server returned invalid registration options")
      }

      options.challenge = base64urlToBuffer(options.challenge)
      if (options.user && options.user.id) {
        options.user.id = base64urlToBuffer(options.user.id)
      }
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(cred => ({
          ...cred,
          id: base64urlToBuffer(cred.id)
        }))
      }

      const credential = await navigator.credentials.create({ publicKey: options })

      const attObj = credential.response.attestationObject
      const clientDataJSON = credential.response.clientDataJSON
      const rawId = credential.rawId

      const data = {
        credential: {
          id: credential.id,
          rawId: bufferToBase64url(rawId),
          type: credential.type,
          response: {
            attestationObject: bufferToBase64url(attObj),
            clientDataJSON: bufferToBase64url(clientDataJSON)
          }
        }
      }

      const verifyRes = await fetch(this.registerVerifyUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify(data),
        credentials: "same-origin"
      })
      const verifyJson = await verifyRes.json()
      if (verifyRes.ok) {
        this.closeAnyModal()
        this.toast("Passkey added", "success")
      } else {
        this.toast(verifyJson.message || "Failed to add passkey", "error")
        this.consoleError("Passkey add verify failed", verifyJson)
      }
    } catch (e) {
      this.toast(e.message || "Failed to add passkey", "error")
      this.consoleError("Passkey add error", e)
      this.alert(e.message || "Failed to add passkey")
    }
  }

  async login(event) {
    event?.preventDefault?.()
    try {
      const params = new URLSearchParams()
      if (this.hasEmailTarget && this.emailTarget.value) {
        params.set("email", this.emailTarget.value)
      }
      const res = await fetch(`${this.loginOptionsUrlValue}?${params.toString()}`, { credentials: "same-origin" })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message || "Failed to get passkey options from server")
      }
      const options = await res.json()

      if (!options.challenge || typeof options.challenge !== 'string') {
        throw new Error("Server returned invalid passkey options")
      }

      options.challenge = base64urlToBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials
          .filter(cred => cred.id && typeof cred.id === 'string')
          .map(cred => ({
            ...cred,
            id: base64urlToBuffer(cred.id)
          }))
      }

      const assertion = await navigator.credentials.get({ publicKey: options })

      const authData = assertion.response.authenticatorData
      const clientDataJSON = assertion.response.clientDataJSON
      const rawId = assertion.rawId
      const sig = assertion.response.signature
      const userHandle = assertion.response.userHandle

      const data = {
        credential: {
          id: assertion.id,
          rawId: bufferToBase64url(rawId),
          type: assertion.type,
          response: {
            authenticatorData: bufferToBase64url(authData),
            clientDataJSON: bufferToBase64url(clientDataJSON),
            signature: bufferToBase64url(sig),
            userHandle: userHandle ? bufferToBase64url(userHandle) : null
          }
        }
      }

      const verifyRes = await fetch(this.loginVerifyUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify(data),
        credentials: "same-origin"
      })
      const json = await verifyRes.json()
      if (verifyRes.ok) {
        window.location = json.redirect_url
      } else {
        this.toast(json.message || "Passkey sign-in failed", "error")
        this.consoleError("Passkey login verify failed", json)
      }
    } catch (e) {
      this.toast(e.message || "Passkey sign-in failed", "error")
      this.consoleError("Passkey login error", e)
      this.alert(e.message || "Passkey sign-in failed")
    }
  }

  csrfToken() {
    const tag = document.querySelector('meta[name="csrf-token"]')
    return tag && tag.getAttribute('content')
  }

  closeAnyModal() {
    const open = document.querySelector('dialog[open]')
    if (open && open.close) open.close()
  }

  toast(message, type = "info") {
    const event = new CustomEvent("toast", { detail: { message, type } })
    window.dispatchEvent(event)
  }

  consoleError(message, payload) {
    try { console.error(`[passkeys] ${message}`, payload) } catch(_) {}
  }

  alert(message) {
    try { window.alert(message) } catch(_) {}
  }
}

